import { PriceSegment, PricePart, PriceFunctions, PriceFunction, PriceElems, PriceSummary } from '@icc/shared/types/price/Prices';
import { Injectable, Inject, Injector } from '@angular/core';
import { APP_CONFIG, AppConfigFactory } from '@icc/shared/config';
import { core, logger, PricesDataService } from '@icc/shared';
import { Common } from '@icc/shared/helpers';
import { ConfiguratorsDataService } from '@icc/configurator/shared';
import { isValueSegment } from '@icc/shared/types/price/PriceSegment';
import { TaxRatesService } from '@icc/b2b/shared';
import { Promotion } from 'libs/b2b/shared/src/lib/interfaces/promotion.interface';

/**
 * Fabryka bazowych funkcji do wyceny.
 *
 * @export
 * @class PriceBaseService
 */
@Injectable({
    providedIn: 'root'
})
export class PriceBaseService2 {
    private counter = 0;
    /**
     * Creates an instance of PriceBaseService.
     *
     * @param {Injector} injector Injector
     * @param {any} Core Core
     * @param {any} IccConfig IccConfig
     *
     * @memberOf PriceBaseService
     */
    constructor(
        private injector: Injector,
        private configuratorsDataService: ConfiguratorsDataService,
        private pricesDataService: PricesDataService,
        @Inject(APP_CONFIG) private config: AppConfigFactory
    ) {}

    /**
     * Ustawia cenę w konfiguracji.
     *
     * @param {any} config Konfiguracja
     * @returns {number} Cena
     *
     * @memberOf PriceBaseService
     */
    setPrice(config, pricesSettings) {
        const type = config.type;
        let configPriceElems = core.copy(PriceElems[type]) || {};
        if (Common.isString(configPriceElems)) {
            configPriceElems = core.copy(PriceElems[configPriceElems]) || {};
        }

        this.counter = 0;
        const configNoPriceCauses = [];
        const configNoPriceElems = {
            accessories: [],
            sashAccessories: [],
            sideAccessories: [],
            complementaryGoods: {
                windowsill: [],
                cassonetto: [],
                glass: [],
                accessory: [],
                profile: [],
            },
        };

        let price = 0;

        try {
            const priceStack = this.calculate(
                config,
                pricesSettings,
                configPriceElems,
                configNoPriceCauses,
                configNoPriceElems,
            );
            price = this.standardActions(
                config,
                priceStack,
                configPriceElems,
                configNoPriceElems,
                configNoPriceCauses,
                pricesSettings
            );
        } catch (err) {
            price = NaN;
            logger.error(err);
            config.Price = NaN;
        }

        config.PriceValid = true;
        return price;
    }

    /**
     * Ustawia cenę w konfiguracji, wyliczając ją z wartości ustawionych w segmentach.
     *
     * @param {any} config Konfiguracja
     * @returns {number} Cena
     *
     * @memberOf PriceBaseService
     */
    setPreCalculatedPrice(config, pricesSettings) {
        const type = config.type;
        let configPriceElems = core.copy(PriceElems[type]) || {};
        if (Common.isString(configPriceElems)) {
            configPriceElems = core.copy(PriceElems[configPriceElems]) || {};
        }

        this.counter = 0;
        const configNoPriceCauses = [];
        const configNoPriceElems = {
            accessories: [],
            sashAccessories: [],
            sideAccessories: [],
            complementaryGoods: {
                windowsill: [],
                cassonetto: [],
                glass: [],
                accessory: [],
                profile: [],
            },
        };

        let price = 0;

        try {
            const priceSegments = (config.priceSegments || config.PriceSegments || []).filter(segment => segment.type !== 'market' && segment.type !== 'charge');
            for (const seg of priceSegments) {
                seg.value = seg.baseValue;
            }
            price = this.standardActions(
                config,
                (config.priceSegments || config.PriceSegments || []).filter(segment => segment.type !== 'market' && segment.type !== 'charge'),
                configPriceElems,
                configNoPriceElems,
                configNoPriceCauses,
                pricesSettings
            );
        } catch (err) {
            price = NaN;
            logger.error(err);
            config.Price = NaN;
        }

        config.PriceValid = true;
        return price;
    }

    /**
     * Standardowe akcje po kazdym wyliczaniu cen: marza, wspolczynniki, przypisanie do konfiguracji
     * @param  {object} conf          Konfiguracja
     * @param  {number} price         Wyliczona cena
     * @param  {object} PriceElems    Elementy skladowe ceny
     * @param  {object} NoPriceElems  Elementy ktorych cen nie da sie policzyc
     * @param  {array}  NoPriceCauses Powody niewyliczenia ceny
     */
    standardActions(
        conf,
        priceStack: PriceSegment[],
        configPriceElems,
        NoPriceElems,
        NoPriceCauses,
        pricesSettings: {
            charge?: number,
            taxRate?: number,
            marketFactor?: number,
            isGross?: boolean,
            multiplier?: number,
            priceVersionId?: number,
            contractorCompanyId?: number | null,
            promotion?: Promotion;
        }
    ) {

        const priceStackNoMargin = core.copy(priceStack);

        const pricePartsNoMargin = this.getPriceParts(priceStackNoMargin);
        const summaryNoMargin = this.summarizePriceParts(pricePartsNoMargin, pricesSettings);
        conf.PricePartsNoMargin = pricePartsNoMargin;
        conf.PriceElemsNoMargin = core.copy(configPriceElems);
        conf.PriceAfterMultiplierNoMargin = summaryNoMargin.totalBeforeDiscountsAndPromotions;
        conf.PriceNoMargin = summaryNoMargin.totalAfterPromotions;
        conf.PriceSegmentsNoMargin = priceStackNoMargin;

        let multiplier = 1;

        if (pricesSettings.charge) {
            multiplier = multiplier * pricesSettings.charge;
        }
        if (pricesSettings.marketFactor) {
            multiplier = multiplier * pricesSettings.marketFactor;
        }
        if (pricesSettings.multiplier) {
            multiplier = multiplier * pricesSettings.multiplier;
        }
        priceStack = this.multiplyPriceStack(priceStack, multiplier);

        const priceParts = this.getPriceParts(priceStack);
        const summary = this.summarizePriceParts(priceParts, pricesSettings);
        conf.PriceAfterMultiplier = summary.totalBeforeDiscountsAndPromotions;
        conf.Price = summary.totalAfterPromotions;
        conf.PriceIsGross = pricesSettings.isGross;
        conf.PriceTaxRate = summary.tax.taxRate;

        conf.PriceElems = configPriceElems;
        conf.NoPriceElems = NoPriceElems;
        conf.NoPriceCauses = NoPriceCauses;
        conf.PriceParts = priceParts;
        conf.PriceSegments = priceStack;
        conf.PriceAfterDiscounts = summary.totalAfterDiscounts;

        return summary.totalAfterPromotions;
    }

    /**
     * Wyznacza schemat liczenia ceny.
     *
     * @param {any} config              Konfiguracja
     * @param {any} configPriceElems    Elementy wyceny
     * @param {any} configNoPriceCauses Powody braku wyceny
     * @param {any} configNoPriceElems  Elementy bez ceny
     * @returns {PriceSegment[]} Schemat liczenia ceny
     *
     * @memberOf PriceBaseService
     */
    calculate(
        config,
        pricesSettings,
        configPriceElems,
        configNoPriceCauses,
        configNoPriceElems,
    ): PriceSegment[] {
        const type = config.type;
        let functions = PriceFunctions.priceFn[type] || [];
        if (Common.isString(functions)) {
            functions = PriceFunctions.priceFn[functions] || [];
        }
        let mistakeProductFunctions = PriceFunctions.mistakeProductFn[type] || [];
        if (Common.isString(functions)) {
            mistakeProductFunctions =
                PriceFunctions.mistakeProductFn[mistakeProductFunctions] || [];
        }
        const priceStack: PriceSegment[] = [];
        if (config.IsMistakeProduct) {
            priceStack.push({
                type: 'mistakeProduct',
                baseValue: core.num(config.Price),
                value: core.num(config.Price),
                valueType: 'value',
                data: {},
            });
        }
        for (const funcName of functions) {
            if (config.IsMistakeProduct && mistakeProductFunctions.indexOf(funcName) === -1) {
                continue;
            }
            const func = PriceFunctions.map.get(funcName);
            const funcData = {};
            const funcThis: any = this.injector.get(func.class);
            if (func && func.requiredData) {
                for (const i in func.requiredData) {
                    const data = core.deepFind(
                        {
                            conf: config,
                            pricesSettings,
                            prices: this.pricesDataService.getConfiguratorPricesForPriceVersionSync(pricesSettings.priceVersionId, pricesSettings.contractorCompanyId),
                            priceRasters: this.pricesDataService.getPriceRastersForPriceVersionSync(pricesSettings.priceVersionId),
                        },
                        func.requiredData[i]
                    );
                    funcData[i] = data;
                }
            }
            const funcBinded: PriceFunction = func.bind(funcThis);
            priceStack.push(
                ...this.extendId(
                    funcBinded(
                        {
                            PriceStack: priceStack,
                            PriceElems: configPriceElems,
                            NoPriceCauses: configNoPriceCauses,
                            NoPriceElems: configNoPriceElems,
                        },
                        funcData
                    )
                )
            );
        }
        return priceStack;
    }


    getPriceParts(priceStack: PriceSegment[]) {
        const priceParts: PricePart[] = [];
        if (Common.isArray(this.config().IccConfig.Configurators.price.order)) {
            const order = this.config().IccConfig.Configurators.price.order;
            priceStack = priceStack
                .filter(s => s)
                .sort((a, b) => {
                    if (order.indexOf(a.type) > -1 && order.indexOf(b.type) > -1) {
                        return order.indexOf(a.type) - order.indexOf(b.type);
                    } else if (order.indexOf(a.type) === -1) {
                        return 1;
                    } else {
                        return -1;
                    }
                });
        }
        priceStack.map<PriceSegment>(this.extendTo(priceStack).bind(this));
        priceStack
            .filter(s => s && s.valueType === 'multiplier')
            .forEach(s => {
                priceStack
                    .filter(t => t && t.valueType === 'percent')
                    .filter(this.filterTo(s.to).bind(this))
                    .forEach(t => {
                        if (
                            !isNaN(parseFloat(t.value as any))
                            && !isNaN(parseFloat(s.baseValue as any))
                        ) {
                            t.value = (t.value - 1) * parseFloat(s.baseValue as any) + 1;
                        } else if (s.baseValue === null || Number.isNaN(s.baseValue)) {
                            t.value = null;
                        }
                    });
            });
        priceStack
            .filter(s => s && s.valueType === 'percent')
            .forEach(s => {
                priceStack
                    .filter(t => t && t.valueType === 'percent')
                    .filter(this.filterTo(s.to).bind(this))
                    .forEach(t => {
                        if (
                            !isNaN(parseFloat(t.value as any))
                            && !isNaN(parseFloat(s.baseValue as any))
                        ) {
                            t.value =
                                parseFloat(t.value as any) + parseFloat(s.baseValue as any) - 1;
                        } else if (s.baseValue === null || Number.isNaN(s.baseValue)) {
                            t.value = null;
                        }
                    });
            });
        priceStack
            .filter(s => s && s.valueType === 'multiplier')
            .forEach(s => {
                priceStack
                    .filter(isValueSegment)
                    .filter(this.filterTo(s.to).bind(this))
                    .forEach(t => {
                        if (
                            !isNaN(parseFloat(t.value as any))
                            && !isNaN(parseFloat(s.value as any))
                        ) {
                            priceParts.push({
                                valueType: t.type,
                                percentType: s.type,
                                baseUnitValue: t.value,
                                baseValueCurrency: t.valueCurrency,
                                baseValueIsGross: t.valueIsGross,
                                baseValueTaxRate: Number(t.taxRate),
                                basePercent: s.value - 1,
                                unitPrice: t.value * (s.value - 1),
                                valueId: t.id,
                                percentId: s.id,
                                quantity: t.quantity ?? 1,
                            });
                            t.value = t.value * s.value;
                        } else if (s.value === null || Number.isNaN(s.value)) {
                            t.value = null;
                            priceParts.push({
                                valueType: t.type,
                                percentType: s.type,
                                baseUnitValue: t.value,
                                baseValueCurrency: t.valueCurrency,
                                baseValueIsGross: t.valueIsGross,
                                baseValueTaxRate: Number(t.taxRate),
                                basePercent: null,
                                unitPrice: null,
                                valueId: t.id,
                                percentId: s.id,
                                quantity: t.quantity ?? 1,
                            });
                        }
                    });
            });
        priceStack
            .filter(s => s && s.valueType === 'percent')
            .forEach(s => {
                priceStack
                    .filter(isValueSegment)
                    .filter(this.filterTo(s.to).bind(this))
                    .forEach(t => {
                        let tValue = t.value;
                        if (this.isMultipliedByBaseValue(s, t)) {
                            tValue = t.baseValue;
                        }
                        if (
                            !isNaN(parseFloat(tValue as any))
                            && !isNaN(parseFloat(t.value as any))
                            && !isNaN(parseFloat(s.value as any))
                        ) {
                            priceParts.push({
                                valueType: t.type,
                                percentType: s.type,
                                baseUnitValue: tValue,
                                baseValueCurrency: t.valueCurrency,
                                baseValueIsGross: t.valueIsGross,
                                baseValueTaxRate: Number(t.taxRate),
                                basePercent: s.value - 1,
                                unitPrice: tValue * (s.value - 1),
                                valueId: t.id,
                                percentId: s.id,
                                quantity: t.quantity ?? 1,
                            });
                            t.value = parseFloat(t.value as any) + tValue * (s.value - 1);
                        } else if (s.value === null || Number.isNaN(s.value)) {
                            t.value = null;
                            priceParts.push({
                                valueType: t.type,
                                percentType: s.type,
                                baseUnitValue: tValue,
                                baseValueCurrency: t.valueCurrency,
                                baseValueIsGross: t.valueIsGross,
                                baseValueTaxRate: Number(t.taxRate),
                                basePercent: null,
                                unitPrice: null,
                                valueId: t.id,
                                percentId: s.id,
                                quantity: t.quantity ?? 1,
                            });
                        }
                    });
            });
        priceStack
            .filter(isValueSegment)
            .forEach((cur) => {
                priceParts.push({
                    valueType: cur.type,
                    percentType: null,
                    baseUnitValue: parseFloat(cur.baseValue as any),
                    baseValueCurrency: cur.valueCurrency,
                    baseValueIsGross: cur.valueIsGross,
                    baseValueTaxRate: Number(cur.taxRate),
                    basePercent: null,
                    unitPrice: parseFloat(cur.baseValue as any),
                    valueId: cur.id,
                    percentId: null,
                    quantity: cur.quantity ?? 1,
                });
            }, 0);

        return priceParts;
    }

    summarizePriceParts(priceParts: PricePart[], priceSettings: {
        taxRate?: number,
        isGross?: boolean,
        multiplier?:number,
        priceVersionId?: number,
        contractorCompanyId?: number | null,
        promotion?: Promotion;
    }): PriceSummary {
        const summary: PriceSummary = {
            currency: '',
            totalBeforeDiscountsAndPromotions: null,
            promotions: [],
            totalAfterPromotions: null,
            discounts: [],
            totalAfterPositionsDiscounts: null,
            additionalDiscount: {
                discount: 0,
                value: 0
            },
            totalAfterDiscounts: null,
            tax: {
                taxRate: priceSettings.taxRate ?? 0,
                value: 0,
                tax: 0,
            },
            totalGrossAfterDiscounts: null,
            nonValuated: false,
        }

        for (const part of priceParts) {
            if (!isNaN(parseFloat(part.unitPrice as any))) {
                part.unitPrice = TaxRatesService.recalcPrice(
                    priceSettings.isGross,
                    part.unitPrice,
                    part.baseValueIsGross,
                    part.baseValueTaxRate,
                    priceSettings.taxRate,
                );
                part.isGross = priceSettings.isGross ?? false;
                part.taxRate = priceSettings.taxRate;
                part.priceBeforePromotions = core.round(part.unitPrice * (part.quantity ?? 1));
                if (priceSettings.promotion) {
                    part.promotion = {
                        name: priceSettings.promotion.name,
                        promotion: priceSettings.promotion.value,
                        baseValue: part.priceBeforePromotions,
                        value: core.round(part.priceBeforePromotions * priceSettings.promotion.value / 100),
                        type: 'main',
                        promotionType: priceSettings.promotion.type,
                        external: false,
                    };
                    part.priceBeforeDiscounts = part.priceBeforePromotions - part.promotion.value;

                    if (summary.promotions.length === 0) {
                        summary.promotions.push({
                            name: priceSettings.promotion.name,
                            basePrice: part.priceBeforePromotions,
                            basePriceCurrency: part.baseValueCurrency,
                            price: part.priceBeforeDiscounts,
                            value: part.promotion.value,
                            baseValue: part.promotion.baseValue,
                            promotionType: priceSettings.promotion.type,
                            promotion: part.promotion.promotion,
                            external: false,
                        });
                    } else {
                        summary.promotions[0].baseValue += part.promotion.baseValue;
                        summary.promotions[0].value += part.promotion.value;
                        summary.promotions[0].basePrice += part.priceBeforePromotions;
                        summary.promotions[0].basePriceCurrency = part.baseValueCurrency;
                        summary.promotions[0].price += part.priceBeforeDiscounts;
                    }
                } else {
                    part.priceBeforeDiscounts = part.priceBeforePromotions;
                }
                part.priceAfterDiscounts = part.priceBeforeDiscounts;

                summary.totalBeforeDiscountsAndPromotions += core.round(
                    part.priceBeforePromotions
                );

                summary.totalAfterPromotions += core.round(
                    part.priceBeforeDiscounts
                );

                summary.totalAfterDiscounts += core.round(
                    part.priceAfterDiscounts
                );

                summary.tax.value += part.priceAfterDiscounts;

            } else {
                part.unitPrice = null;
                part.priceBeforePromotions = null;
                part.priceBeforeDiscounts = null;
                part.priceAfterDiscounts = null;
                summary.nonValuated = true;
            }
        }

        if (Number.isNaN(summary.totalAfterDiscounts)) {
            summary.totalAfterDiscounts = null;
        }

        if (summary.nonValuated) {
            summary.totalBeforeDiscountsAndPromotions = null;
            summary.totalAfterPromotions = null;
            summary.totalAfterDiscounts = null;
            summary.totalGrossAfterDiscounts = null;
        } else {
            if (priceSettings.isGross) {
                summary.tax.tax = core.round(
                    (summary.tax.taxRate / 100) * TaxRatesService.recalcToNetPrice(summary.tax.value, summary.tax.taxRate)
                );
            } else {
                summary.tax.tax = core.round((summary.tax.taxRate / 100) * summary.tax.value);
            }

            if (priceSettings.isGross) {
                summary.totalGrossAfterDiscounts = summary.totalAfterDiscounts;
            } else {
                summary.totalGrossAfterDiscounts = summary.totalAfterDiscounts + summary.tax.tax;
            }
        }

        return summary;
    }

    /**
     * Funkcja do sortowania segmentów ceny.
     *
     * @param {any} seg1 Segment poprzedni
     * @param {any} seg2 Segment następny
     * @returns {number} Porządek
     *
     * @memberOf PriceBaseService
     */
    sort(seg1, seg2) {
        if (seg1.valueType === 'value') {
            return -1;
        } else if (seg2.valueType === 'value') {
            return 1;
        } else {
            return 0;
        }
    }

    /**
     * Rozszerza segment o informację, do czego ma być doliczany.
     *
     * @param {PriceSegment[]} segments Schemat wyceny.
     * @returns
     *
     * @memberOf PriceBaseService
     */
    extendTo(segments: PriceSegment[]) {
        return (data: PriceSegment) => {
            let types: any = '';
            if (data) {
                if (data.valueType === 'percent' || data.valueType === 'multiplier') {
                    if (
                        Common.isArray(this.config().IccConfig.Configurators.price[data.type])
                        || Common.isString(this.config().IccConfig.Configurators.price[data.type])
                    ) {
                        types = core.copy(this.config().IccConfig.Configurators.price[data.type]);
                    }
                    if (Common.isObject(this.config().IccConfig.Configurators.price[data.type])) {
                        const systemSeg = segments.filter(s => s && s.type === 'system');
                        if (
                            systemSeg.length > 0
                            && (Common.isArray(
                                this.config().IccConfig.Configurators.price[data.type][
                                    systemSeg[0].data.type
                                ]
                            )
                                || Common.isString(
                                    this.config().IccConfig.Configurators.price[data.type][
                                        systemSeg[0].data.type
                                    ]
                                ))
                        ) {
                            types = core.copy(
                                this.config().IccConfig.Configurators.price[data.type][
                                    systemSeg[0].data.type
                                ]
                            );
                        }
                    }
                }

                if (Common.isUndefined(data.to)) {
                    data.to = types;
                } else {
                    if (
                        data.to !== 'allValue'
                        && data.to !== 'allSups'
                        && data.to !== 'allValueOrPercentToBase'
                        && Common.isArray(types)
                    ) {
                        let to = data.to;
                        if (!Common.isArray(data.to)) {
                            to = [data.to];
                        }
                        if (Common.isArray(to) && to.length > 0) {
                            const objectTypes = types.filter(type => Common.isObject(type));
                            const matchedToSegment = Common.isArray(to)
                                ? to
                                      .map(t => {
                                          const matchedTypes = objectTypes.filter(type => {
                                              const matchedSegments = segments.filter(
                                                  this.filterTo([type]).bind(this)
                                              );
                                              return (
                                                  matchedSegments.filter(seg => {
                                                      if (
                                                          Common.isString(t)
                                                          && t === '#' + this.getHash(seg)
                                                      ) {
                                                          return true;
                                                      } else if (
                                                          Common.isNumber(t)
                                                          && t === seg.id
                                                      ) {
                                                          return true;
                                                      }
                                                      return false;
                                                  }).length > 0
                                              );
                                          });
                                          if (matchedTypes.length > 0) {
                                              const newT = core.copy(matchedTypes[0]);
                                              newT.to = t;
                                              return newT;
                                          }
                                      })
                                      .filter(t => t)
                                : [];
                            if (objectTypes.length > 0 && matchedToSegment.length > 0) {
                                data.to = matchedToSegment;
                            }
                        }
                    }
                }
            }
        };
    }

    /**
     * Filtruje segmenty
     *
     * @param {any} to Doczego ma być doliczony segment.
     * @returns
     *
     * @memberOf PriceBaseService
     */
    filterTo(to) {
        if (to === 'allValue') {
            return (seg: PriceSegment) => seg.valueType === 'value';
        }
        if (to === 'allValueOrPercentToBase') {
            return (seg: PriceSegment) =>
                seg.valueType === 'value' || this.isToBasePercentSegment(seg);
        }
        if (to === 'allSupps') {
            return (seg: PriceSegment) =>
                ['sashes', 'rollerBoxes', 'system', 'margin', 'market', 'marketMargin'].indexOf(
                    seg.type
                ) === -1;
        }
        if (!Array.isArray(to)) {
            to = [to];
        }
        const checkHash = to.some(t => Common.isString(t) && t[0] === '#');
        return (seg: PriceSegment) => {
            let match = false;
            for (const t of to) {
                if (
                    Common.isString(t)
                    && (t === seg.type || (checkHash && t === '#' + this.getHash(seg)))
                ) {
                    match = true;
                    break;
                } else if (Common.isNumber(t) && t === seg.id) {
                    match = true;
                    break;
                } else if (Common.isObject(t)) {
                    let matchValues = true;
                    for (const path in t) {
                        if (path === 'toBase') {
                            continue;
                        }
                        if (path === 'to') {
                            matchValues =
                                matchValues && [seg].some(this.filterTo(t[path]).bind(this));
                            continue;
                        }
                        matchValues = matchValues && core.deepFind(seg, path) === t[path];
                        if (!matchValues) {
                            break;
                        }
                    }
                    if (matchValues) {
                        match = true;
                        break;
                    }
                }
            }
            return match;
        };
    }

    isToBasePercentSegment(segment: PriceSegment) {
        if (segment.valueType !== 'percent') {
            return false;
        }
        let to = segment.to;
        if (!Array.isArray(to)) {
            to = [to];
        }
        return to.some(t => Common.isObject(t) && t.toBase);
    }

    /**
     * Rozszerza segmenty o id.
     *
     * @param {PriceSegment[]} data Schemat wyceny
     * @returns {PriceSegment[]} Schemat wyceny z id.
     *
     * @memberOf PriceBaseService
     */
    extendId(data: PriceSegment[]) {
        if (Common.isArray(data)) {
            data.map(d => {
                if (d) {
                    d.id = this.counter++;
                }
            });
        }
        return data;
    }

    isMultipliedByBaseValue(percentSeg: PriceSegment, valueSeg: PriceSegment) {
        return (
            Common.isArray(percentSeg.to)
            && percentSeg.to
                .filter((t: any) => {
                    return Common.isObject(t);
                })
                .some((t: any) => {
                    let match = false;
                    let matchValues = true;
                    for (const path in t) {
                        if (path === 'toBase') {
                            continue;
                        }
                        if (path === 'to') {
                            continue;
                        }
                        matchValues = matchValues && core.deepFind(valueSeg, path) === t[path];
                        if (!matchValues) {
                            break;
                        }
                    }
                    if (matchValues) {
                        match = Boolean(t.toBase);
                    }
                    return match;
                })
        );
    }

    /**
     * Zwraca hash danego segmentu.
     *
     * @param {PriceSegment} seg Segment
     * @returns {string} Hash
     *
     * @memberOf PriceBaseService
     */
    getHash(seg: PriceSegment) {
        const hashedObj = {
            type: seg.type,
            baseValue: seg.baseValue,
            data: seg.data,
        };
        return JSON.stringify(hashedObj);
    }

    /**
     * Zwraca elementy wyceny ze schematu wyceny.
     *
     * @param {PriceSegment[]} priceStack Schemat wyceny
     * @returns {object} Elementy wyceny
     *
     * @memberOf PriceBaseService
     */
    getPriceElems(priceStack: PriceSegment[]) {
        return priceStack.reduce((prev, seg) => {
            if (Common.isUndefined(prev[seg.type])) {
                prev[seg.type] = [];
            }
            prev[seg.type].push(
                Common.extend(
                    {
                        price: seg.value,
                    },
                    seg.data
                )
            );
            return prev;
        }, {});
    }

    multiplyPriceStack(priceStack: PriceSegment[], multiplier: number) {
        return priceStack.map(seg => {
            if (seg.valueType === 'value' && seg.value !== null) {
                seg.baseValue = seg.baseValue * multiplier;
                seg.value = seg.value * multiplier;
            }
            return seg;
        });
    }
}
